import apisauce from 'apisauce'
import Config from 'APP/Config'
import type { LocationView } from '@dialogue/services/src/waldo/model'
import { isAccessTokenValid } from 'APP/Lib/AuthHelpers'
import { logger } from 'APP/Lib/Datadog'

type Location = {
  latitude: number
  longitude: number
}

const baseURL = Config.WALDO_ENDPOINT

const create = (accessToken: string | null) => {
  if (!isAccessTokenValid(accessToken)) {
    throw new Error('Unable to connect to Waldo. Access token is not valid.')
  }

  const api = apisauce.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    timeout: 20000,
  })

  const getUserLocation = async (userId: string): Promise<LocationView[]> => {
    const response = await api.get<LocationView[]>(`/${userId}`)
    if (response.ok && response.data) {
      return response.data
    }
    const errorMessage = 'Error getting user location from Waldo'
    logger.error(errorMessage, {
      ok: response?.ok,
      problem: response?.problem,
      status: response?.status,
    })
    throw new Error(errorMessage)
  }

  const setUserLocation = async (userId: string, location: Location): Promise<LocationView> => {
    const response = await api.post<LocationView>(`/${userId}`, location)
    if (response.ok && response.data) {
      return response.data
    }
    const errorMessage = 'Error setting user location in Waldo'
    logger.error(errorMessage, {
      ok: response?.ok,
      problem: response?.problem,
      status: response?.status,
    })
    throw new Error(errorMessage)
  }

  const setUserLocationNotShared = async (userId: string): Promise<LocationView> => {
    const response = await api.post<LocationView>(`/location_not_shared/${userId}`)
    if (response.ok && response.data) {
      return response.data
    }
    const errorMessage = 'Error setting user location as not shared in Waldo'
    logger.error(errorMessage, {
      ok: response?.ok,
      problem: response?.problem,
      status: response?.status,
    })
    throw new Error(errorMessage)
  }

  return {
    getUserLocation,
    setUserLocation,
    setUserLocationNotShared,
  }
}

export default {
  create,
}
